import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ChevronRight,
  Download,
  Home,
  Loader2,
  ReceiptText,
} from "lucide-react";
import { getSocioPagosConSos, descargarFacturaPdf } from "../services/cuotasService.tsx";

type MovimientoSosItem = {
  idcomprobante?: string | number;
  fecha?: string;
  clipro?: string;
  memo?: string;
  fcncnd?: string;
  letra?: string;
  sucursal?: number;
  numero?: number;
  montodebe?: number;
  montohaber?: number;
  montosaldo?: number;
  estadoasociacion?: number;
  idtipo_operacion?: number;
};

type TipoMovimiento = "cuota" | "servicio" | "caja";

const tipoColor: Record<TipoMovimiento, string> = {
  cuota: "bg-indigo-50 text-indigo-700 border-indigo-200",
  servicio: "bg-emerald-50 text-emerald-700 border-emerald-200",
  caja: "bg-amber-50 text-amber-700 border-amber-200",
};

const money = (value: number) =>
  `$${Number(value || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const inferTipoMovimiento = (m: MovimientoSosItem): TipoMovimiento => {
  const op = Number(m.idtipo_operacion);
  if ([12, 14].includes(op)) return "cuota";
  if ([4].includes(op)) return "caja";
  const text = `${m.memo || ""} ${m.fcncnd || ""} ${m.letra || ""}`.toLowerCase();
  if (text.includes("cuota") || text.includes("social")) return "cuota";
  if (text.includes("caja") || text.includes("cofre")) return "caja";
  return "servicio";
};

const movimientoImporte = (m: MovimientoSosItem) =>
  Number(m.montohaber || 0) > 0 ? Number(m.montohaber || 0) : Number(m.montodebe || 0);

const esRecibo = (m: MovimientoSosItem) => Number(m.montohaber || 0) > 0;

const facturaCompensada = (m: MovimientoSosItem) =>
  !esRecibo(m) && Number(m.estadoasociacion || 0) === 3;

const saldoPendienteMovimiento = (m: MovimientoSosItem) => {
  if (esRecibo(m) || facturaCompensada(m)) return 0;
  return Math.max(0, Number(m.montosaldo ?? m.montodebe ?? 0));
};

const movimientoPendiente = (m: MovimientoSosItem) =>
  !esRecibo(m) && saldoPendienteMovimiento(m) > 0;

const getSosFechaHasta = () => `${new Date().getFullYear()}-12-31`;

export default function MovimientosPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [movimientos, setMovimientos] = useState<MovimientoSosItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<"todos" | TipoMovimiento>("todos");
  const [documentFilter, setDocumentFilter] = useState<"todos" | "facturas" | "recibos">("todos");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortFecha, setSortFecha] = useState<"desc" | "asc">("desc");
  const [descargandoIdx, setDescargandoIdx] = useState<number | null>(null);

  const socioSeleccionado = useMemo(() => {
    const stored = localStorage.getItem("socioSeleccionado");
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const stored = localStorage.getItem("socioSeleccionado");
        if (!stored) throw new Error("No hay socio seleccionado");

        const socio = JSON.parse(stored);
        const socioId = socio?.socio_id;
        if (!socioId) throw new Error("No se encontró el ID del socio");

        const detalle = await getSocioPagosConSos(socioId, getAccessTokenSilently, {
          includeSosMovimientos: true,
        });

        setMovimientos(Array.isArray(detalle?.movimientos_sos) ? detalle.movimientos_sos : []);
      } catch (err: any) {
        setError(err?.message || "No se pudieron cargar los movimientos.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [getAccessTokenSilently]);

  const resumen = useMemo(() => {
    const base = {
      deudaTotal: 0,
      saldoCuenta: 0,
      saldoAFavor: 0,
      totalFacturado: 0,
      totalPagado: 0,
      cuotas: 0,
      servicios: 0,
      cajas: 0,
      pendientes: 0,
      compensados: 0,
    };

    movimientos.forEach((m) => {
      const debe = Number(m.montodebe || 0);
      const haber = Number(m.montohaber || 0);
      const saldo = saldoPendienteMovimiento(m);
      const tipo = inferTipoMovimiento(m);

      base.totalFacturado += debe;
      base.totalPagado += haber;

      if (saldo > 0) {
        base.pendientes += 1;
        if (tipo === "cuota") base.cuotas += saldo;
        if (tipo === "servicio") base.servicios += saldo;
        if (tipo === "caja") base.cajas += saldo;
      } else {
        base.compensados += 1;
      }
    });

    base.saldoCuenta = Number((base.totalFacturado - base.totalPagado).toFixed(2));
    base.deudaTotal = Math.max(0, base.saldoCuenta);
    base.saldoAFavor = Math.max(0, -base.saldoCuenta);

    return base;
  }, [movimientos]);

  const filtrados = useMemo(() => {
    return movimientos
      .filter((m) => {
        const tipo = inferTipoMovimiento(m);
        if (selectedType !== "todos" && tipo !== selectedType) return false;

        const esRecibo = Number(m.montohaber || 0) > 0;
        if (documentFilter === "facturas") return !esRecibo;
        if (documentFilter === "recibos") return esRecibo;
        return true;
      })
      .sort((a, b) => {
        const aTime = new Date(a.fecha || 0).getTime();
        const bTime = new Date(b.fecha || 0).getTime();
        return sortFecha === "desc" ? bTime - aTime : aTime - bTime;
      });
  }, [movimientos, selectedType, documentFilter, sortFecha]);

  const totalPages = Math.max(1, Math.ceil(filtrados.length / pageSize));
  const rangoInicio = filtrados.length ? (page - 1) * pageSize + 1 : 0;
  const rangoFin = filtrados.length ? Math.min(page * pageSize, filtrados.length) : 0;
  const paginados = filtrados.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [selectedType, documentFilter, pageSize]);

  const handleDescargarPdf = async (m: MovimientoSosItem, idx: number) => {
    setDescargandoIdx(idx);
    try {
      const comp = [m.fcncnd, m.letra, m.sucursal, m.numero]
        .filter((x) => x !== null && x !== undefined && String(x) !== "")
        .join("-");
      const tipo = inferTipoMovimiento(m);
      const pendiente = movimientoPendiente(m);
      const reciboVal = esRecibo(m);
      const saldo = saldoPendienteMovimiento(m);
      const estadoLabel = reciboVal ? "Pago recibido" : pendiente ? "Pendiente" : "Compensado";

      await descargarFacturaPdf(
        {
          fecha: m.fecha,
          documento: reciboVal ? "Recibo" : "Factura",
          comprobante: comp || String(m.idcomprobante || ""),
          detalle: m.memo || `Tipo op. ${m.idtipo_operacion ?? "-"}`,
          tipo: tipo === "cuota" ? "Cuota" : tipo === "caja" ? "Caja" : "Servicio",
          movimiento: reciboVal ? "Pago recibido" : pendiente ? "Cargo generado" : "Cargo compensado",
          importe: movimientoImporte(m),
          saldo,
          estado: estadoLabel,
          socioNombre: socioSeleccionado?.socio_nombre || socioSeleccionado?.nombre,
          socioId: socioSeleccionado?.socio_id,
        },
        getAccessTokenSilently
      );
    } catch (error) {
      console.error("Error al descargar PDF:", error);
    } finally {
      setDescargandoIdx(null);
    }
  };

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-10">
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Movimientos</span>
              </div>
            </li>
          </ol>
        </nav>

        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl">
          <div className="px-6 py-7 md:px-10 lg:px-12">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-100">
                  <ReceiptText className="h-4 w-4" />
                  Movimientos del socio
                </p>
                <h1 className="mt-2 text-3xl font-bold leading-tight sm:text-4xl">Movimientos completos</h1>
              </div>
              <div className="rounded-xl bg-white/15 px-4 py-3 text-sm font-semibold shadow-sm">
                <p className="text-[11px] uppercase tracking-wide text-indigo-100">
                  {resumen.saldoAFavor > 0 ? "Saldo a favor" : "Saldo pendiente"}
                </p>
                <p className="text-lg">
                  {resumen.saldoAFavor > 0
                    ? `Saldo: -${money(resumen.saldoAFavor)}`
                    : resumen.deudaTotal > 0
                      ? `Tenés deuda: ${money(resumen.deudaTotal)}`
                      : "Sin deuda"}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-indigo-100">
                  <span>Facturado: {money(resumen.totalFacturado)}</span>
                  <span>Pagado: {money(resumen.totalPagado)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Todos los movimientos</p>
              <p className="text-xs text-slate-500">
                Pendientes: {resumen.pendientes} · Compensados/pagos: {resumen.compensados}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-full bg-slate-50 px-2 py-1">
              {[
                { key: "todos", label: "Todos" },
                { key: "cuota", label: "Cuotas" },
                { key: "servicio", label: "Servicios" },
                { key: "caja", label: "Cajas" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setSelectedType(f.key as any)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    selectedType === f.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
            <div className="flex flex-wrap items-center gap-2 rounded-full bg-slate-50 px-2 py-1">
              {[
                { key: "todos", label: "Todos los documentos" },
                { key: "facturas", label: "Solo facturas" },
                { key: "recibos", label: "Solo recibos" },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setDocumentFilter(f.key as any)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    documentFilter === f.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span>
                Mostrando {filtrados.length ? `${rangoInicio}-${rangoFin}` : 0} de {filtrados.length}
              </span>
              <label className="font-semibold" htmlFor="mov-sort-fecha">Orden</label>
              <select
                id="mov-sort-fecha"
                value={sortFecha}
                onChange={(e) => setSortFecha(e.target.value as "desc" | "asc")}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm"
              >
                <option value="desc">Más recientes</option>
                <option value="asc">Más antiguos</option>
              </select>
              <label className="font-semibold" htmlFor="mov-page-size">Por página</label>
              <select
                id="mov-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm"
              >
                {[5, 10, 15, 20].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          {loading && (
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              Obteniendo movimientos del socio...
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Fecha</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Documento</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Comprobante</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Detalle</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Tipo</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Movimiento</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Importe</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600">Saldo</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600">Estado</th>
                  <th className="px-3 py-2 text-center font-semibold text-slate-600">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading
                  ? Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={`mov-skeleton-${idx}`} className="animate-pulse">
                        {Array.from({ length: 10 }).map((__, col) => (
                          <td key={col} className="px-3 py-3">
                            <div className="h-3 rounded bg-slate-200" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : paginados.map((m, idx) => {
                      const comp = [m.fcncnd, m.letra, m.sucursal, m.numero]
                        .filter((x) => x !== null && x !== undefined && String(x) !== "")
                        .join("-");
                      const tipo = inferTipoMovimiento(m);
                      const pendiente = movimientoPendiente(m);
                      const recibo = esRecibo(m);
                      const compensada = facturaCompensada(m);
                      const saldo = saldoPendienteMovimiento(m);
                      const estadoLabel = recibo ? "Pago recibido" : pendiente ? "Pendiente" : "Compensado";

                      return (
                        <tr key={`${m.idcomprobante || "mov"}-${idx}`}>
                          <td className="px-3 py-2 text-slate-700">
                            {m.fecha ? new Date(m.fecha).toLocaleDateString("es-AR") : "-"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${recibo ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                              {recibo ? "Recibo" : "Factura"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{comp || m.idcomprobante || "-"}</td>
                          <td className="px-3 py-2 text-slate-600">{m.memo || `Tipo op. ${m.idtipo_operacion ?? "-"}`}</td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tipoColor[tipo]}`}>
                              {tipo === "cuota" ? "Cuota" : tipo === "caja" ? "Caja" : "Servicio"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {recibo ? "Pago recibido" : compensada ? "Cargo compensado" : "Cargo generado"}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            {money(movimientoImporte(m))}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900">
                            {money(saldo)}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${pendiente ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                              {estadoLabel}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => handleDescargarPdf(m, (page - 1) * pageSize + idx)}
                              disabled={descargandoIdx === (page - 1) * pageSize + idx}
                              className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 shadow-sm transition hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 disabled:cursor-not-allowed disabled:opacity-40"
                              title="Descargar comprobante PDF"
                            >
                              {descargandoIdx === (page - 1) * pageSize + idx ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>

          {!loading && !filtrados.length && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              No hay movimientos para mostrar.
            </div>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
            <button
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-lg border border-slate-200 px-3 py-2 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="font-semibold text-slate-700">Página {page} de {totalPages}</span>
            <button
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              className="rounded-lg border border-slate-200 px-3 py-2 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
